import { Alert, Box } from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";

/**
 * The "you cannot edit your own permissions" notice shared by Account Access and Feature Access —
 * My Info's FR-030/FR-036, User Manager's FR-009/FR-055.
 *
 * **The rule is the same in both areas; whether it applies is not.** The rule is "a user cannot
 * edit their own access permissions" (spec Clarifications). The reason is **not** impersonation.
 *
 * - In **My Info** the subject is always self, so the notice is **unconditional** and does not vary
 *   between the ordinary and "as" cases.
 * - In **User Manager** the subject is a selected row, so it is **conditional**: it renders only
 *   when `selectedUserId === operator.userId` (FR-009, FR-055).
 *
 * *This comment previously stated the unconditional case as the reason the component exists, which
 * was true while My Info was the only caller and became wrong the moment the panel was extracted.
 * Rewritten with the extraction (tasks.md T011), one of the two comments the plan flagged as
 * becoming wrong on arrival.*
 *
 * **The condition is not this component's to evaluate.** User Manager decides it at the shell as
 * **one comparison** and passes no `authority` to the permission tabs when it holds — SC-005's
 * criterion is zero paths by which a user can change their own access permissions through the page,
 * and a flag threaded through the tree is more places for that to go wrong. Do not add an
 * `isSelf`-style prop here.
 *
 * Copy comes from `labels.json` (`READ_ONLY_PERMISSIONS_NOTICE`) so all three locales resolve
 * through Label Services — **Configuration Principle VI** (Localized Labels — No Hardcoded UI
 * Text), FR-049. A tab needing different wording passes `message`, which must itself be a
 * localized string from `labels.json` — never a literal.
 */
export interface ReadOnlyNoticeProps {
  /**
   * Optional override for the shared notice, for a tab whose wording must differ. MUST be a
   * localized string; a raw literal here would bypass FR-049 and render untranslated in `fr-CA`.
   */
  message?: string;
  testId?: string;
}

/**
 * Rendered as an Interstate `Alert` (**Configuration Principle III** — Interstate &
 * Core-Components Only) with `role="status"` so assistive technology announces it. `status`
 * rather than `alert`: this is a standing property of the tab, not an error or a response to
 * something the user just did, so it should not interrupt.
 *
 * `type="caution"` — Interstate's name for the amber treatment the approved prototype uses here
 * (`alert-warning`); its `AlertSeverity` union has no `"warning"` member, and the prop's own doc
 * comment claiming otherwise is stale. Amber rather than `"info"` because this notice states a
 * **limit** on what the user can do, which is the one thing on these two tabs they might otherwise
 * spend time trying to work around. Every neighbouring notice on the page is informational blue, so
 * blue here made the constraint read as another aside.
 *
 * The notice is presentation only. It explains why the controls are inert; it does not enforce it.
 * The controls themselves carry `disabled`, and real authorization stays server-side
 * (**Monorepo Principle VII** — Security & Data Protection).
 */
export const ReadOnlyNotice = ({ message, testId }: ReadOnlyNoticeProps) => (
  <Box data-testid={testId ?? "read-only-notice"}>
    <Alert role={"status"} type={"caution"}>
      {message ?? (Locale.getText("READ_ONLY_PERMISSIONS_NOTICE") as string)}
    </Alert>
  </Box>
);
